import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'

import { Carousel, Image, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import {
  ArrowRightCircleFill,
  BookmarkStarFill,
  CheckCircleFill,
  PlusCircleFill,
  StarFill,
  TrashFill,
} from 'react-bootstrap-icons'

import { CustomButton } from '../../../../CustomButton'
import { CustomSpinner } from '../../../../CustomSpinner'

import ImagensService from '../../../../../services/database/ImagensService'

import { sendErrorApiMessage } from '../../../../../utils/sendErrorApiMessage'

import {
  Center,
  ContainerComponent,
  ContentComponent,
  FooterComponent,
  NextButton,
} from '../../styles'

import { toast } from 'react-toastify'

import { IconButton } from '../../../../IconButton'
import { Modable } from '../../../../Modable'
import { ImageButtons, ImageContent, Images, TextContent } from './styles'

export const Imagens = forwardRef(
  ({ isVisible, onNext, product: { SKU, CODPRODUTO }, loadProduct }, ref) => {
    const [images, setImages] = useState([])
    const [isLoadingImages, setIsLoadingImages] = useState(true)

    const [isSaveImagesLoading, setIsSaveImagesLoading] = useState(false)
    const [isUpdateImageLoading, setIsUpdateImageLoading] = useState({})
    const [isDeleteImageLoading, setIsDeleteImageLoading] = useState({})

    const [modalImages, setModalImages] = useState({
      isOpen: false,
      index: null,
    })

    const [isDeleteAllImagesLoading, setIsDeleteAllImagesLoading] =
      useState(false)

    const inputRef = useRef(null)

    const loadImages = useCallback(async () => {
      if (CODPRODUTO) {
        setIsLoadingImages(true)

        try {
          const imagesData = await ImagensService.getAllByProductId(CODPRODUTO)

          setImages(imagesData)
        } catch (err) {
          sendErrorApiMessage(err, 'Erro ao carregar as imagens!')
        } finally {
          setIsLoadingImages(false)
        }
      }
    }, [CODPRODUTO])

    async function handleSaveImages(e) {
      e.preventDefault()

      try {
        for (const image of images.filter((img) => img.FAKE_ID)) {
          setIsSaveImagesLoading(true)

          const data = new FormData()

          data.append('sku', SKU)
          data.append('caminho', image.file)
          data.append('idProduto', CODPRODUTO)

          await ImagensService.create(data)
        }

        toast.success('Imagem inserida com sucesso!')

        loadImages()
      } catch (err) {
        sendErrorApiMessage(err, 'Houve um erro ao cadastrar a(s) imagem(ns)!')
      } finally {
        setIsSaveImagesLoading(false)
      }
    }

    function handleFileSelected() {
      if (inputRef.current?.files.length > 0) {
        for (const file of inputRef.current.files) {
          if (file) {
            const reader = new FileReader()

            reader.onload = function (e) {
              const data = {
                FAKE_ID: Math.round(Math.random() * 1000),
                IMAGE_URL: e.target.result,
                NOME: file.name,
                SKU,
                ID_PRODUTO: CODPRODUTO,
                file,
              }

              setImages((prevState) => [...prevState, data])
            }

            reader.readAsDataURL(file)
          }
        }
      }
    }

    async function handleDeleteAllImages() {
      setImages((prevState) => prevState.filter((img) => !img.FAKE_ID))

      try {
        setIsDeleteAllImagesLoading(true)

        await ImagensService.deleteAll(CODPRODUTO)
      } catch (err) {
        sendErrorApiMessage(err, 'Houve um erro ao deletar as imagens!')
      } finally {
        setIsDeleteAllImagesLoading(false)
      }

      loadProduct()
      toast.success('Todas as imagens foram deletadas com sucesso!')
    }

    async function handleChangeImageApp(id, index) {
      setIsUpdateImageLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      try {
        await ImagensService.updateImgApp(id)

        toast.success('Imagem alterada para padrão!')
        loadProduct()
      } catch (err) {
        sendErrorApiMessage(err, 'Erro ao colocar imagem como capa padrão!')
      } finally {
        setIsUpdateImageLoading((prevState) => ({
          ...prevState,
          [index]: false,
        }))
      }
    }

    function handleSelectImage(selectedIndex) {
      setModalImages((prevState) => ({
        ...prevState,
        index: selectedIndex,
      }))
    }

    async function handleDeleteImagem(imagem, index) {
      if (imagem.FAKE_ID) {
        setImages((prevState) =>
          prevState.filter((img) => img.FAKE_ID !== imagem.FAKE_ID),
        )
      } else {
        setIsDeleteImageLoading((prevState) => ({
          ...prevState,
          [index]: true,
        }))

        try {
          await ImagensService.delete(imagem.ID)

          toast.success('Imagem deletada com sucesso!')
          loadProduct()
        } catch (err) {
          sendErrorApiMessage(err, 'Erro ao deletar imagem!')
        } finally {
          setIsDeleteImageLoading((prevState) => ({
            ...prevState,
            [index]: false,
          }))
        }
      }
    }

    useEffect(() => {
      if (isVisible) {
        loadImages()
      }
    }, [loadImages, isVisible])

    if (!isVisible) {
      return null
    }

    return (
      <>
        <ContainerComponent ref={ref}>
          <div>
            <ContentComponent>
              {isLoadingImages ? (
                <Center
                  style={{
                    justifyContent: 'flex-start',
                  }}
                >
                  <CustomSpinner />
                  <p>Carregando Imagens...</p>
                </Center>
              ) : (
                <>
                  <h4>Imagens</h4>

                  {images.length === 0 ? (
                    <Row className="mb-3">
                      <p>Sem imagens, adicione algumas para começar!</p>
                    </Row>
                  ) : (
                    <Images>
                      {images.map((image, index) => (
                        <ImageContent key={image.ID || image.FAKE_ID}>
                          <Image
                            src={
                              image?.CAMINHO
                                ? `${process.env.REACT_APP_IMAGE_URL_API}/${image?.CAMINHO}`
                                : image.IMAGE_URL
                            }
                            width="100%"
                            height={150}
                            onClick={() =>
                              setModalImages({
                                isOpen: true,
                                index,
                              })
                            }
                            style={{
                              cursor: 'pointer',
                            }}
                          />

                          <TextContent>
                            <p>{image?.NOME || image?.CAMINHO}</p>
                          </TextContent>

                          <ImageButtons>
                            {image.IMG_APP === 'SIM' && (
                              <OverlayTrigger
                                overlay={<Tooltip>Imagem Padrão</Tooltip>}
                              >
                                <StarFill color="#ff5500" />
                              </OverlayTrigger>
                            )}

                            <main>
                              {image.ID && (
                                <OverlayTrigger
                                  overlay={
                                    <Tooltip>
                                      Colocar como imagem padrão
                                    </Tooltip>
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      handleChangeImageApp(image.ID, index)
                                    }
                                    color="#ff7029"
                                    isLoading={isUpdateImageLoading[index]}
                                  >
                                    <BookmarkStarFill />
                                  </IconButton>
                                </OverlayTrigger>
                              )}

                              <OverlayTrigger
                                overlay={<Tooltip>Excluir Imagem</Tooltip>}
                              >
                                <IconButton
                                  color="#ff0000"
                                  onClick={() =>
                                    handleDeleteImagem(image, index)
                                  }
                                  isLoading={isDeleteImageLoading[index]}
                                >
                                  <TrashFill />
                                </IconButton>
                              </OverlayTrigger>
                            </main>
                          </ImageButtons>
                        </ImageContent>
                      ))}
                    </Images>
                  )}

                  <input
                    ref={inputRef}
                    multiple
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                  />

                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <CustomButton
                      title="Adicionar Imagem"
                      color="#0d6efd"
                      onClick={() => inputRef?.current.click()}
                    >
                      <PlusCircleFill />
                    </CustomButton>

                    <CustomButton
                      title="Salvar Imagem(ns)"
                      color="#299600"
                      onClick={handleSaveImages}
                      isLoading={isSaveImagesLoading}
                      isLoadingText="Salvando..."
                      isDisabled={
                        images.filter((img) => img.FAKE_ID).length === 0
                      }
                    >
                      <CheckCircleFill />
                    </CustomButton>

                    <CustomButton
                      title="Deletar todas as imagens"
                      color="#ff0000"
                      onClick={handleDeleteAllImages}
                      isLoading={isDeleteAllImagesLoading}
                      isLoadingText="Deletando..."
                      isDisabled={images.length === 0}
                    >
                      <TrashFill />
                    </CustomButton>
                  </div>
                </>
              )}
            </ContentComponent>

            {!isLoadingImages && (
              <FooterComponent
                style={{
                  marginTop: 100,
                }}
              >
                <OverlayTrigger overlay={<Tooltip>Avançar</Tooltip>}>
                  <NextButton onClick={onNext}>
                    <ArrowRightCircleFill
                      size={15}
                      color="white"
                    />
                  </NextButton>
                </OverlayTrigger>
              </FooterComponent>
            )}
          </div>
        </ContainerComponent>

        {modalImages.isOpen && (
          <Modable
            isOpen={modalImages.isOpen}
            onClose={() =>
              setModalImages({
                index: null,
                isOpen: false,
              })
            }
          >
            <Carousel
              activeIndex={modalImages.index}
              onSelect={handleSelectImage}
              indicators={false}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
              }}
            >
              {images.map((image) => (
                <Carousel.Item>
                  <Image
                    src={
                      image?.CAMINHO
                        ? `${process.env.REACT_APP_IMAGE_URL_API}/${image?.CAMINHO}`
                        : image.IMAGE_URL
                    }
                    style={{
                      flex: 1,

                      minHeight: 450,
                      maxHeight: 450,
                    }}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          </Modable>
        )}
      </>
    )
  },
)
